import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import cuentaCorrienteService, { CuentaCorrienteSaldo } from '../../services/cuentaCorrienteService';
import { formatCurrency } from '../../utils/formatters';

interface ClienteSaldoSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (cliente: CuentaCorrienteSaldo) => void;
}

const ClienteSaldoSearch: React.FC<ClienteSaldoSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [clientes, setClientes] = useState<CuentaCorrienteSaldo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const trimmed = searchTerm.trim();
            let filtered: CuentaCorrienteSaldo[] = [];
            
            // Usar el parámetro searchTerm del endpoint para búsqueda eficiente
            try {
                const response = await cuentaCorrienteService.getSaldos({
                    pageNumber: 1,
                    pageSize: 50, // Optimizado para mejor rendimiento
                    searchTerm: trimmed || undefined // Pasar el término de búsqueda al backend (o undefined si está vacío)
                });
                
                filtered = response.items;
            } catch (err) {
                console.warn('Error al cargar saldos:', err);
            }

            setClientes(filtered);
            if (filtered.length === 0) {
                setError('No se encontraron clientes.');
            }
        } catch (err: any) {
            setError(`Error al buscar clientes: ${err.message || 'Error desconocido'}`);
            setClientes([]);
        } finally {
            setLoading(false);
        }
    }, [searchTerm]);

    const handleSelect = (cliente: CuentaCorrienteSaldo) => {
        onSelect(cliente);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setClientes([]);
        setError(null);
        onClose();
    };

    // Búsqueda automática con debounce
    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setClientes([]);
            setError(null);
            return;
        }
        
        const handle = setTimeout(() => {
            handleSearch();
        }, 500); // Debounce de 500ms para evitar búsquedas muy frecuentes
        return () => clearTimeout(handle);
    }, [open, searchTerm, handleSearch]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Cliente con Saldo
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Número de cliente o nombre"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && !loading && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                )}

                {!loading && clientes.length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nro. Cliente</TableCell>
                                    <TableCell>Nombre</TableCell>
                                    <TableCell align="right">Saldo Total</TableCell>
                                    <TableCell align="right">Saldo Vigente</TableCell>
                                    <TableCell align="right">Saldo Vencido</TableCell>
                                    <TableCell>Acción</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clientes.map((cliente) => (
                                    <TableRow key={cliente.nroCliente} hover>
                                        <TableCell>{cliente.nroCliente}</TableCell>
                                        <TableCell>{cliente.nombre}</TableCell>
                                        <TableCell align="right">{formatCurrency(cliente.saldoTotal)}</TableCell>
                                        <TableCell align="right">{formatCurrency(cliente.saldoVigente)}</TableCell>
                                        <TableCell align="right">{formatCurrency(cliente.saldoVencido)}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleSelect(cliente)}
                                            >
                                                Seleccionar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ClienteSaldoSearch;

